<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('app-repository')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Upload App</h1>
        </header>

        <section class="module__body">
            <form
                class="full-width"
                @submit.prevent="uploadApp"
            >
                <div class="form-flex">
                    <div class="form-flex-group">
                        <div class="form-group">
                            <label for="type">App Type</label>
                            <select
                                id="type"
                                v-model="type"
                                required
                            >
                                <option value="atak_plugin">goTenna Pro ATAK Plugin</option>
                                <option value="atak_core">ATAK Core</option>
                                <option value="pro_app">goTenna Pro</option>
                                <option value="generic">Generic App</option>
                                <option value="generic_atak_plugin">Generic ATAK Plugin</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label for="display_name">Title</label>
                            <input
                                v-model="display_name"
                                type="text"
                                required
                                maxlength="60"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.version"
                            class="form-group"
                        >
                            <label for="version">Version</label>
                            <input
                                v-model="version"
                                type="text"
                                maxlength="32"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.atak_version"
                            class="form-group"
                        >
                            <label for="atak_version">ATAK Version</label>
                            <input
                                v-model="atak_version"
                                type="text"
                                maxlength="32"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.gotenna_version"
                            class="form-group"
                        >
                            <label for="gotenna_version">goTenna Version</label>
                            <select
                                id="gotenna_version"
                                v-model="gotenna_version"
                            >
                                <option value=""></option>
                                <option
                                    v-for="(userAgent, index) in userAgents"
                                    :key="`${index}`"
                                    :value="userAgent"
                                >
                                    {{ userAgent }}
                                </option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label for="build_number">Build Number</label>
                            <input
                                v-model="build_number"
                                type="text"
                                maxlength="32"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.firmware_version"
                            class="form-group"
                        >
                            <label for="firmware_version">Firmware Version</label>
                            <ToggleButtonList
                                :selectedValues="firmware_version"
                                :allValues="sortedFirmware"
                                @update="firmware_version = $event"
                            />
                        </div>
                    </div>

                    <div class="form-flex-group">
                        <div class="form-group">
                            <label for="release_date">Release Date</label>
                            <input
                                id="release_date"
                                v-model="release_date"
                                type="date"
                                required
                            />
                        </div>

                        <div class="form-group">
                            <label for="file_type">File Type</label>
                            <select
                                id="file_type"
                                v-model="fileType"
                                required
                            >
                                <option value="file">File</option>
                                <option
                                    v-if="linkTypeIsVisible"
                                    value="link"
                                >
                                    Link
                                </option>
                            </select>
                        </div>

                        <div
                            v-show="is_link"
                            class="form-group"
                        >
                            <label for="link">Link</label>
                            <input
                                id="link"
                                v-model="link"
                                type="url"
                                :required="is_link"
                            />
                        </div>

                        <div
                            v-show="!is_link"
                            class="form-group"
                        >
                            <file-upload
                                :url="`/app-repository`"
                                accept=".apk,.ipa,application/vnd.android.package-archive,application/x-itunes-ipa"
                                :required="!is_link"
                                @error="(formErrorResponse) => (errorResponse = formErrorResponse)"
                                @success="tempUploadComplete"
                            />
                        </div>

                        <div class="form-group">
                            <label for="version">Make Visible to Admins</label>
                            <slider-toggle-field
                                name="visibleToAdmins"
                                label=""
                                :value="visible_to_admins"
                                @update="visible_to_admins = $event"
                            ></slider-toggle-field>
                        </div>

                        <div class="form-group">
                            <label for="version">Make Visible to goKit Tablet App</label>
                            <slider-toggle-field
                                name="visibleToGoKits"
                                label=""
                                :value="visible_to_gokits"
                                @update="visible_to_gokits = $event"
                            ></slider-toggle-field>
                        </div>
                    </div>
                </div>

                <form-errors :response="errorResponse" />

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                        :disabled="(!is_link && !tempFileUploaded) || !formLoaded"
                    >
                        <div>Add</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FileUpload from '@/components/FileUpload/index.vue'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import SliderToggleField from '@/components/SliderToggleField.vue'
import ToggleButtonList from '@/components/ToggleButtonList.vue'

export default {
    components: {
        FileUpload,
        FormErrors,
        SliderToggleField,
        ToggleButtonList,
    },

    mixins: [canToast, hasBackButton],

    data() {
        return {
            filenames: {},
            display_name: null,
            version: null,
            visible_to_admins: false,
            visible_to_gokits: false,
            atak_version: null,
            firmware_version: [],
            gotenna_version: null,
            build_number: null,
            type: 'atak_plugin',
            release_date: new Date().toISOString().substring(0, 10),
            errorResponse: null,
            submitted: false,
            tempFileUploaded: false,
            allUserAgents: [],
            sortedFirmware: [],
            formLoaded: false,
            link: null,
            is_link: false,
        }
    },

    computed: {
        fileType: {
            get() {
                return this.is_link ? 'link' : 'file'
            },
            set(type) {
                this.is_link = type === 'link'
            },
        },
        userAgents() {
            let result = []

            let productType = ''

            switch (this.type) {
                case 'pro_app':
                    productType = 'goTennaPro'
                    break
                case 'atak_plugin':
                    productType = 'goTennaATAK'
                    break
                default:
                    break
            }

            if (productType) {
                result = this.allUserAgents
                    .filter((userAgent) => userAgent.product.user_agent === productType)
                    .map((d) => d.version)
            }

            return result
        },
        hiddenFields() {
            const result = {
                atak_version: false,
                firmware_version: false,
                gotenna_version: false,
                version: false,
            }

            switch (this.type) {
                case 'pro_app':
                    result.atak_version = true
                    result.version = true
                    break
                case 'atak_plugin':
                    result.atak_version = true
                    result.version = true
                    break
                case 'atak_core':
                    result.firmware_version = true
                    result.gotenna_version = true
                    result.version = true
                    break
                case 'generic_atak_plugin':
                    result.firmware_version = true
                    result.gotenna_version = true
                    break
                case 'generic':
                    result.firmware_version = true
                    result.gotenna_version = true
                    break
                default:
                    break
            }

            return result
        },

        linkTypeIsVisible() {
            return this.type !== 'pro_app' && this.type !== 'atak_plugin'
        },
    },

    watch: {
        type(newType) {
            if (newType === 'pro_app' || newType === 'atak_plugin') {
                this.is_link = false
            }
        },
    },

    mounted() {
        const gotennaVersion = this.$http.get('user-agents').then(({ data }) => {
            this.allUserAgents = data
        })

        const firmware = this.$http.get('firmware').then(({ data }) => {
            // create sorted array of objects
            // with keys id and label
            this.sortedFirmware = data.data
                .map(({ id, version }) => {
                    return { id, version }
                })
                .sort((a, b) => (a.label > b.label ? 1 : -1))
        })

        Promise.all([gotennaVersion, firmware]).then(() => {
            this.formLoaded = true
        })
    },

    methods: {
        tempUploadComplete(filenames) {
            this.filenames = filenames
            this.errorResponse = null
            this.tempFileUploaded = true
        },

        uploadApp() {
            if ((!this.is_link && !this.tempFileUploaded) || this.submitted || !this.formLoaded) {
                return
            }
            this.submitted = true

            const data = {
                temp_filename: this.filenames.temp,
                original_filename: this.filenames.original,
                display_name: this.display_name,
                type: this.type,
                version: this.version,
                visible_to_admins: Number(this.visible_to_admins),
                visible_to_gokits: Number(this.visible_to_gokits),
                atak_version: this.atak_version,
                firmware_version: this.firmware_version,
                gotenna_version: this.gotenna_version,
                build_number: this.build_number,
                release_date: this.release_date,
                link: this.link,
                is_link: Number(this.is_link),
            }

            // remove hidden fields
            Object.keys(this.hiddenFields)
                .filter((fieldName) => this.hiddenFields[fieldName])
                .forEach((fieldName) => {
                    delete data[fieldName]
                })

            if (!this.linkTypeIsVisible) {
                delete data.link
            }

            this.$http
                .post(`app-repository`, data)
                .then(() => {
                    this.toast('App successfully uploaded!')
                    this.$router.push(`/app-repository`)
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
    },
}
</script>
